import React, { useEffect, useMemo, useState } from "react"
import { Image, Pressable, StyleSheet, View } from "react-native"
import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import {
    anchorPageIndex,
    hasUsableAnchor,
    typeConfig,
} from "./correctionPointTypes"

/**
 * Interactive numbered markers drawn over one page of the student's raw copy.
 *
 * Replaces the baked-in bubbles of the server-rendered correction image:
 * each point's normalized anchor bbox ([0..1], top-left origin) is projected
 * onto the displayed image (resizeMode="contain" math), and the bubble is
 * placed at the TOP-RIGHT corner of the bbox — off the student's writing, so
 * the answer underneath stays readable. Selecting a point outlines its bbox
 * so the tutor sees exactly which zone the explanation talks about.
 *
 * Rendered inside PinchableBox's Zoom, so markers follow zoom/rotation with
 * the image. Positions are computed from the image's natural size
 * (Image.getSize, cached per uri).
 *
 * The overlay self-measures its container (it absolute-fills the image's box,
 * whose flex-driven size is unknown ahead of layout) — no size props needed.
 *
 * @param {Array} points            all correction points (any page)
 * @param {number} pageIndex        which work image this overlay covers
 * @param {number} pagesCount       total work images (for page clamping)
 * @param {string} imageUri         the page's uri (natural-size lookup)
 * @param {boolean} visible         eye-toggle — false hides every marker
 * @param {number|null} selectedIndex the selected point's `index` (1-based)
 * @param {Function} onSelectPoint  (index|null) => void — tap a bubble
 *        selects its point; tapping the selected bubble clears it
 */

// Cache natural sizes at module level: the same copy pages are re-rendered on
// every tab/mode switch and Image.getSize hits the network cache but is async.
const naturalSizeCache = {}

const MARKER_SIZE = 26
const HIT_SLOP = 8

const CorrectionMarkersOverlay = ({
    points,
    pageIndex,
    pagesCount,
    imageUri,
    visible = true,
    selectedIndex = null,
    onSelectPoint,
}) => {
    const [naturalSize, setNaturalSize] = useState(
        naturalSizeCache[imageUri] ?? null
    )
    const [container, setContainer] = useState(null)

    useEffect(() => {
        if (!imageUri) return
        if (naturalSizeCache[imageUri]) {
            setNaturalSize(naturalSizeCache[imageUri])
            return
        }
        let mounted = true
        Image.getSize(
            imageUri,
            (width, height) => {
                naturalSizeCache[imageUri] = { width, height }
                if (mounted) setNaturalSize({ width, height })
            },
            () => {
                // Size lookup failed → no markers for this page (image itself
                // still renders; the analysis list stays fully usable).
            }
        )
        return () => {
            mounted = false
        }
    }, [imageUri])

    const pagePoints = useMemo(
        () =>
            (points ?? []).filter(
                (p) =>
                    hasUsableAnchor(p) &&
                    anchorPageIndex(p, pagesCount) === pageIndex
            ),
        [points, pageIndex, pagesCount]
    )

    const onContainerLayout = (e) => {
        const { width, height } = e.nativeEvent.layout
        if (
            width > 0 &&
            height > 0 &&
            (container?.width !== width || container?.height !== height)
        ) {
            setContainer({ width, height })
        }
    }

    if (!pagePoints.length) {
        return null
    }

    const ready = visible && naturalSize && container

    // resizeMode="contain": the image is scaled to fit and centered.
    const scale = ready
        ? Math.min(
              container.width / naturalSize.width,
              container.height / naturalSize.height
          )
        : 1
    const displayedWidth = ready ? naturalSize.width * scale : 0
    const displayedHeight = ready ? naturalSize.height * scale : 0
    const offsetX = ready ? (container.width - displayedWidth) / 2 : 0
    const offsetY = ready ? (container.height - displayedHeight) / 2 : 0

    const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

    return (
        <View
            style={StyleSheet.absoluteFill}
            pointerEvents="box-none"
            onLayout={onContainerLayout}
        >
            {ready &&
                pagePoints.map((point) => {
                const cfg = typeConfig(point?.type)
                const a = point.anchor
                const box = {
                    left: offsetX + a.x * displayedWidth,
                    top: offsetY + a.y * displayedHeight,
                    width: a.w * displayedWidth,
                    height: a.h * displayedHeight,
                }
                const selected = selectedIndex === point?.index

                // Bubble at the bbox's top-right corner, kept inside the
                // displayed image bounds so it never drifts into the gutter.
                const bubbleLeft = clamp(
                    box.left + box.width - MARKER_SIZE / 2,
                    offsetX,
                    offsetX + displayedWidth - MARKER_SIZE
                )
                const bubbleTop = clamp(
                    box.top - MARKER_SIZE / 2,
                    offsetY,
                    offsetY + displayedHeight - MARKER_SIZE
                )

                return (
                    <React.Fragment key={point?.index}>
                        {selected && box.width > 2 && box.height > 2 && (
                            <View
                                pointerEvents="none"
                                style={[
                                    styles.zoneOutline,
                                    {
                                        left: box.left,
                                        top: box.top,
                                        width: box.width,
                                        height: box.height,
                                        borderColor: cfg.color,
                                    },
                                ]}
                            />
                        )}
                        <Pressable
                            hitSlop={HIT_SLOP}
                            onPress={() =>
                                onSelectPoint?.(selected ? null : point?.index)
                            }
                            style={[
                                styles.marker,
                                {
                                    left: bubbleLeft,
                                    top: bubbleTop,
                                    backgroundColor: cfg.color,
                                    opacity: selected ? 1 : 0.82,
                                    transform: [{ scale: selected ? 1.15 : 1 }],
                                },
                                selected && styles.markerSelected,
                            ]}
                        >
                            <TextM
                                fontWeight="Bold"
                                fontSize={13}
                                color={theme.colors.white}
                            >
                                {point?.index}
                            </TextM>
                        </Pressable>
                    </React.Fragment>
                )
            })}
        </View>
    )
}

const styles = StyleSheet.create({
    marker: {
        position: "absolute",
        width: MARKER_SIZE,
        height: MARKER_SIZE,
        borderRadius: MARKER_SIZE / 2,
        borderWidth: 2,
        borderColor: theme.colors.white,
        alignItems: "center",
        justifyContent: "center",
        // Slight lift so bubbles read as UI, not as part of the photo.
        shadowColor: "#000",
        shadowOpacity: 0.25,
        shadowRadius: 3,
        shadowOffset: { width: 0, height: 1 },
        elevation: 3,
    },
    markerSelected: {
        borderWidth: 2.5,
    },
    zoneOutline: {
        position: "absolute",
        borderWidth: 2,
        borderRadius: 6,
        borderStyle: "dashed",
        backgroundColor: "rgba(255, 255, 255, 0.001)",
    },
})

export default CorrectionMarkersOverlay
